import React, { useMemo } from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import GettingStarted from '../assets/Software/getting-started-software.svg';
import IDEImage from '../assets/GridCard/ide.svg';
import PLCide from '../assets/plc-ide.svg';

import Banner from '../components/Banner/Banner';
import Layout from '../components/Layout';
import PageTitle from '../components/PageTitle/PageTitle';
import SEO from '../components/seo';

import SideBar from '../components/SideBar/SideBar';
import ConnectAndContribute from '../components/ConnectAndContribute/ConnectAndContribute';

import './software.scss';
import { IconArrowRightTeal2, IconArrowUpRightTeal, getIcon } from '../components/Icons';
import { getSoftwareSideBarDataRefactored } from '../utils/functions';
import PageSubtitle from '../components/PageSubtitle/PageSubtitle';
import { SOFTWARE_IDE_PAGE, SOFTWARE_PLC_PAGE } from '../utils/constants';

interface SoftwareProps {
  file: {
    childMdx: any;
    frontmatter: {
      title: string;
    };
  };
  softwarePages: any;
  softwareSubpages: any;
}

export default function Software(): React.ReactElement {
  const data: SoftwareProps = useStaticQuery(pageQuery);

  const softwareRefactoredData = useMemo(() => {
    return getSoftwareSideBarDataRefactored(data);
  }, [data]);

  return (
    <Layout>
      <SEO title="Software" description="Learn about the IDEs, Web Editor, CLI and all the software tools that you need to get your hands dirty." />
      <main className="page-container">
        <SideBar links={[]} nestedCategories={softwareRefactoredData} actualPage={'Software tools'} />
        <div className="page-layout software">
          <PageTitle
            title="Software tools"
            description="Learn about the IDEs, Web Editor, CLI and all the software tools that you need to get your hands dirty."
          />
          <Banner
            title="Arduino IDE"
            description="Discover all the features of the Arduino IDE, our most popular programming tool."
            image={IDEImage}
            cta={[{ url: SOFTWARE_IDE_PAGE, label: 'DISCOVER MORE', icon: null, external: false }]}
            bottom={true}
          />
          <Banner
            title="Arduino CLI"
            description="Arduino CLI is a multifunctional tool with Boards/Library Managers, sketch builder for Arduino-compatible boards via command line."
            image={GettingStarted}
            cta={[{ url: 'https://arduino.github.io/arduino-cli/0.34/', label: 'getting started', icon: <IconArrowUpRightTeal />, external: true }]}
          />
          <Banner
            title="Arduino PLC IDE"
            description="The Arduino solution to program PLC compatible Arduino devices using IEC 61131-3 standard languages (Windows only). Downloadable for free."
            image={PLCide}
            cta={[
              {
                url: SOFTWARE_PLC_PAGE,
                label: 'DISCOVER MORE',
                icon: null,
                external: false,
              },
            ]}
          />
          <PageSubtitle title="Browse Tutorials" removeBottom={true} />
          <section className="software__columns">
            {softwareRefactoredData &&
              softwareRefactoredData.map((softwareCategory) => {
                return (
                  <div key={softwareCategory.firstLevelCategory} className="software__columns-item">
                    <h3>
                      <span>{getIcon(softwareCategory.image)}</span> {softwareCategory.firstLevelCategory}
                    </h3>
                    {softwareCategory.nestedCategories.map((release) => {
                      return release.nestedChildren.map((el, i) => {
                        if (i >= 5) return null;
                        return (
                          <div key={el.url} className="software__columns-links">
                            <a href={el.url}>{el.title}</a>
                          </div>
                        );
                      });
                    })}
                    <a
                      href={softwareCategory.firstLevelCategory === 'PLC IDE' ? SOFTWARE_PLC_PAGE : SOFTWARE_IDE_PAGE}
                      className="software__nav-links"
                    >
                      view all <IconArrowRightTeal2 className="software__nav-icon" />
                    </a>
                  </div>
                );
              })}
          </section>
          <ConnectAndContribute />
        </div>
      </main>
    </Layout>
  );
}

export const pageQuery = graphql`
  {
    softwarePages: allFile(filter: { fields: { type: { eq: "software" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
          }
          childMdx {
            frontmatter {
              title
              description
              icon
            }
          }
        }
      }
    }
    softwareSubpages: allFile(filter: { fields: { type: { eq: "softwarepage" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
          }
          childMdx {
            frontmatter {
              title
              description
              icon
            }
          }
        }
      }
    }
  }
`;
